import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, type Engagement } from '../lib/api'
import { PageHead, Stat, Skel, Empty } from '../components/ui'
import { money, thaiDate } from '../lib/format'

type ClaimStatus = 'submitted' | 'approved' | 'rejected' | 'paid'

interface ExpensePolicy {
  category: string
  limit_satang: number
  requires_receipt: boolean
  updated_at: string
}

interface ExpenseClaim {
  id: string
  claim_no: string
  claimant: string
  category: string
  amount_satang: number
  claimed_at: string
  receipt_sha256: string
  receipt_note: string
  status: ClaimStatus
  created_at: string
  updated_at: string
}

interface ExpenseStats {
  policies: number
  claims: number
  submitted: number
  approved: number
  rejected: number
  paid: number
  total_satang: number
  flagged: number
  flagged_satang: number
  over_limit: number
  missing_receipt: number
  duplicate_claims: number
  weekend: number
}

interface ScanHit {
  rule_code: string
  title: string
  summary: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  risk_score: number
  amount_at_risk_satang: number
}

interface ScanResult {
  engagement_id: string
  generated_at: string
  claims: number
  policies: number
  new_findings: number
  existing_findings: number
  hits: ScanHit[] | null
}

const statusLabel: Record<ClaimStatus, string> = {
  submitted: 'ส่งแล้ว',
  approved: 'อนุมัติแล้ว',
  rejected: 'ไม่อนุมัติ',
  paid: 'จ่ายแล้ว',
}

const severityLabel: Record<string, string> = {
  low: 'ต่ำ', medium: 'ปานกลาง', high: 'สูง', critical: 'วิกฤต',
}

const today = () => new Date().toLocaleDateString('en-CA')

/** Baht typed by a human, stored as integer satang. Never keep the float. */
function toSatang(baht: string): number {
  const n = Number(baht.replace(/,/g, ''))
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.round(n * 100)
}

export function Expenses() {
  const qc = useQueryClient()
  const [claimOpen, setClaimOpen] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState<string | null>(null)
  const [policyCategory, setPolicyCategory] = useState('')
  const [policyLimit, setPolicyLimit] = useState('')
  const [policyReceipt, setPolicyReceipt] = useState(true)
  const [claimNo, setClaimNo] = useState('')
  const [claimant, setClaimant] = useState('')
  const [category, setCategory] = useState('')
  const [amount, setAmount] = useState('')
  const [claimedAt, setClaimedAt] = useState(today)
  const [receiptSha, setReceiptSha] = useState('')
  const [receiptNote, setReceiptNote] = useState('')
  const [status, setStatus] = useState<ClaimStatus>('submitted')
  const [engagementId, setEngagementId] = useState('')

  const policies = useQuery({
    queryKey: ['expense-policies'],
    queryFn: () => api<ExpensePolicy[]>('/v1/expenses/policies'),
    staleTime: 20_000,
  })
  const claims = useQuery({
    queryKey: ['expense-claims'],
    queryFn: () => api<ExpenseClaim[]>('/v1/expenses/claims'),
    staleTime: 15_000,
  })
  const stats = useQuery({
    queryKey: ['expense-stats'],
    queryFn: () => api<ExpenseStats>('/v1/expenses/stats'),
    staleTime: 15_000,
  })
  const engagements = useQuery({
    queryKey: ['engagements'],
    queryFn: () => api<Engagement[]>('/v1/engagements'),
    staleTime: 60_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['expense-claims'] })
    void qc.invalidateQueries({ queryKey: ['expense-stats'] })
  }

  const savePolicy = useMutation({
    mutationFn: () => apiWrite<ExpensePolicy>('/v1/expenses/policies', 'PUT', {
      category: policyCategory,
      limit_satang: toSatang(policyLimit),
      requires_receipt: policyReceipt,
    }),
    onSuccess: () => {
      setEditingPolicy(null)
      setPolicyCategory('')
      setPolicyLimit('')
      setPolicyReceipt(true)
      void qc.invalidateQueries({ queryKey: ['expense-policies'] })
      void qc.invalidateQueries({ queryKey: ['expense-stats'] })
    },
  })

  const saveClaim = useMutation({
    mutationFn: () => apiWrite<ExpenseClaim>('/v1/expenses/claims', 'POST', {
      claim_no: claimNo,
      claimant,
      category,
      amount_satang: toSatang(amount),
      claimed_at: claimedAt,
      receipt_sha256: receiptSha,
      receipt_note: receiptNote,
      status,
    }),
    onSuccess: () => {
      setClaimOpen(false)
      setClaimNo('')
      setClaimant('')
      setAmount('')
      setReceiptSha('')
      setReceiptNote('')
      setStatus('submitted')
      refresh()
    },
  })

  const scan = useMutation({
    mutationFn: () => apiWrite<ScanResult>('/v1/expenses/scan', 'POST', { engagement_id: engagementId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['findings'] })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const rows = claims.data ?? []
  const policyRows = policies.data ?? []
  const s = stats.data

  const policyFor = (name: string) => policyRows.find((p) => p.category.toLowerCase() === name.trim().toLowerCase())

  const startPolicy = (p?: ExpensePolicy) => {
    setEditingPolicy(p ? p.category : null)
    setPolicyCategory(p?.category ?? '')
    setPolicyLimit(p ? String(p.limit_satang / 100) : '')
    setPolicyReceipt(p ? p.requires_receipt : true)
    savePolicy.reset()
  }

  return (
    <>
      <PageHead
        title="เบิกค่าใช้จ่าย"
        sub="ตรวจใบเบิกกับวงเงินนโยบายรายหมวด · เก็บจำนวนเงินเป็นสตางค์ ไม่มีทศนิยมลอยตัว"
        actions={
          <button className="btn btn-primary" onClick={() => setClaimOpen((v) => !v)}>
            {claimOpen ? 'ยกเลิก' : 'เพิ่มใบเบิก'}
          </button>
        }
      />

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="ใบเบิกทั้งหมด" value={s ? s.claims : '—'} unit="ใบ" foot={s ? `ส่งแล้ว ${s.submitted} · อนุมัติ ${s.approved} · จ่ายแล้ว ${s.paid}` : 'กำลังโหลด'} />
        <Stat label="ยอดเบิกรวม" value={s ? money(s.total_satang) : '—'} foot={s ? `ครอบคลุม ${s.policies} หมวดนโยบาย` : 'กำลังโหลด'} />
        <Stat
          label="ต้องตรวจสอบ"
          value={<span style={{ color: s && s.flagged > 0 ? 'var(--color-amber)' : undefined }}>{s ? s.flagged : '—'}</span>}
          unit="ใบ"
          foot={s ? `มูลค่า ${money(s.flagged_satang)} · ใบเสร็จซ้ำ ${s.duplicate_claims}` : 'กำลังโหลด'}
        />
      </div>

      <div className="card card-pad" style={{ marginBottom: 18 }}>
        <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="card-label">สแกนนโยบายทั้งองค์กร</div>
            <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
              ข้อสังเกตจะถูกบันทึกเข้าโครงการที่เลือก และไม่สร้างซ้ำเมื่อสแกนอีกครั้ง
            </div>
          </div>
          <select aria-label="กรองตามสถานะ" className="select" style={{ minWidth: 260 }} value={engagementId} onChange={(e) => setEngagementId(e.target.value)}>
            <option value="">เลือกโครงการตรวจสอบ</option>
            {(engagements.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.code} · {e.title}</option>)}
          </select>
          <button
            className="btn btn-primary"
            disabled={scan.isPending || !engagementId}
            onClick={() => scan.mutate()}
          >
            {scan.isPending ? 'กำลังสแกน…' : 'สแกนนโยบาย'}
          </button>
        </div>
        {scan.error && (
          <div className="sev sev-critical" style={{ marginTop: 12 }}>
            <i className="sev-dot" />{(scan.error as Error).message}
          </div>
        )}
        {scan.data && (
          <div style={{ marginTop: 14 }}>
            <div className="dim" style={{ fontSize: 12 }}>
              ตรวจ {scan.data.claims} ใบ เทียบ {scan.data.policies} หมวด · ข้อสังเกตใหม่ {scan.data.new_findings} รายการ
              {scan.data.existing_findings > 0 ? ` · มีอยู่แล้ว ${scan.data.existing_findings} รายการ` : ''}
            </div>
            {(scan.data.hits ?? []).length === 0 ? (
              <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>ไม่พบรายการที่ผิดนโยบายในรอบนี้</div>
            ) : (
              <div className="table-wrap" style={{ marginTop: 10 }}>
                <table className="data">
                  <thead><tr><th>กฎ</th><th>เรื่อง</th><th>ความรุนแรง</th><th>มูลค่าเสี่ยง</th></tr></thead>
                  <tbody>
                    {(scan.data.hits ?? []).map((h) => (
                      <tr key={h.rule_code + h.title + h.summary} style={{ cursor: 'default' }}>
                        <td className="mono">{h.rule_code}</td>
                        <td>
                          <div>{h.title}</div>
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>{h.summary}</div>
                        </td>
                        <td><span className={`sev sev-${h.severity}`}><i className="sev-dot" />{severityLabel[h.severity] ?? h.severity}</span></td>
                        <td className="num">{money(h.amount_at_risk_satang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {claimOpen && (
        <div className="card card-pad rise" style={{ marginBottom: 16 }}>
          <div className="grid-2">
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="claim-no">เลขที่ใบเบิก</label>
              <input id="claim-no" className="input" style={{ width: '100%' }} maxLength={40}
                value={claimNo} onChange={(e) => setClaimNo(e.target.value)} placeholder="EXP-2026-006" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="claim-claimant">ผู้เบิก</label>
              <input id="claim-claimant" className="input" style={{ width: '100%' }} maxLength={200}
                value={claimant} onChange={(e) => setClaimant(e.target.value)} placeholder="ชื่อ-นามสกุล" />
            </div>
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="claim-category">หมวดค่าใช้จ่าย</label>
              <input id="claim-category" className="input" style={{ width: '100%' }} maxLength={80} list="expense-categories"
                value={category} onChange={(e) => setCategory(e.target.value)} placeholder="เช่น เดินทาง" />
              <datalist id="expense-categories">
                {policyRows.map((p) => <option key={p.category} value={p.category} />)}
              </datalist>
            </div>
            <div className="field">
              <label htmlFor="claim-amount">จำนวนเงิน (บาท)</label>
              <input id="claim-amount" className="input tnum" style={{ width: '100%' }} type="number" min="0" step="0.01"
                value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="claim-date">วันที่เบิก</label>
              <input id="claim-date" className="input" style={{ width: '100%' }} type="date"
                value={claimedAt} onChange={(e) => setClaimedAt(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="claim-status">สถานะ</label>
              <select id="claim-status" className="select" style={{ width: '100%' }}
                value={status} onChange={(e) => setStatus(e.target.value as ClaimStatus)}>
                <option value="submitted">ส่งแล้ว</option>
                <option value="approved">อนุมัติแล้ว</option>
                <option value="rejected">ไม่อนุมัติ</option>
                <option value="paid">จ่ายแล้ว</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="claim-sha">ลายนิ้วมือไฟล์ใบเสร็จ (SHA-256)</label>
            <input id="claim-sha" className="input mono" style={{ width: '100%' }} maxLength={64}
              value={receiptSha} onChange={(e) => setReceiptSha(e.target.value)} placeholder="เว้นว่างได้ถ้ายังไม่มีไฟล์" />
            <div className="dim" style={{ fontSize: 11, marginTop: 5 }}>
              ใช้ตรวจใบเสร็จซ้ำ · ระบบไม่รับไฟล์ในหน้านี้
            </div>
          </div>
          <div className="field">
            <label htmlFor="claim-note">บันทึกหลักฐาน</label>
            <input id="claim-note" className="input" style={{ width: '100%' }} maxLength={500}
              value={receiptNote} onChange={(e) => setReceiptNote(e.target.value)} placeholder="เช่น ใบเสร็จต้นฉบับอยู่ที่ฝ่ายบัญชี" />
          </div>
          <div className="row" style={{ marginTop: 14, gap: 10 }}>
            <button
              className="btn btn-primary"
              disabled={saveClaim.isPending || !claimNo.trim() || !claimant.trim() || !category.trim() || toSatang(amount) <= 0}
              onClick={() => saveClaim.mutate()}
            >
              {saveClaim.isPending ? 'กำลังบันทึก…' : 'บันทึกใบเบิก'}
            </button>
            {saveClaim.error && <span className="sev sev-critical"><i className="sev-dot" />{(saveClaim.error as Error).message}</span>}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 22 }}>
        <div className="card-label" style={{ marginBottom: 10 }}>ใบเบิกค่าใช้จ่าย</div>
        {claims.isLoading ? <Skel h={240} /> : rows.length === 0 ? (
          <Empty title="ยังไม่มีใบเบิก" body="เริ่มจากกำหนดวงเงินรายหมวด แล้วจึงบันทึกใบเบิกใบแรก" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>เลขที่ใบเบิก</th><th>ผู้เบิก</th><th>หมวด</th><th>จำนวนเงิน</th>
                  <th>วันที่เบิก</th><th>ใบเสร็จ</th><th>เทียบวงเงิน</th><th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const p = policyFor(c.category)
                  const over = p ? c.amount_satang > p.limit_satang : false
                  const noReceipt = p ? p.requires_receipt && !c.receipt_sha256 && !c.receipt_note : false
                  return (
                    <tr key={c.id} style={{ cursor: 'default' }}>
                      <td className="mono">{c.claim_no}</td>
                      <td>{c.claimant}</td>
                      <td className="dim">{c.category}</td>
                      <td className="num">{money(c.amount_satang)}</td>
                      <td className="dim">{thaiDate(c.claimed_at)}</td>
                      <td className="dim">
                        {c.receipt_sha256 ? <span className="mono" title={c.receipt_sha256}>{c.receipt_sha256.slice(0, 10)}…</span>
                          : c.receipt_note ? 'มีบันทึก' : '—'}
                      </td>
                      <td>
                        {over ? <span className="chip gold">เกินวงเงิน</span>
                          : noReceipt ? <span className="chip">ไม่มีใบเสร็จ</span>
                            : <span className="dim" style={{ fontSize: 11.5 }}>ตามนโยบาย</span>}
                      </td>
                      <td>
                        <span
                          className={`chip${c.status === 'approved' || c.status === 'paid' ? ' gold' : ''}`}
                          style={c.status === 'rejected' ? { color: 'var(--color-crimson)' } : undefined}
                        >
                          {statusLabel[c.status] ?? c.status}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">วงเงินตามหมวด</div>
          {policies.isLoading ? <Skel h={120} /> : policyRows.length === 0 ? (
            <div className="dim" style={{ fontSize: 12.5, marginTop: 12 }}>
              ยังไม่มีนโยบาย — หมวดที่ไม่มีนโยบายจะไม่ถูกตรวจ
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="data">
                <thead><tr><th>หมวด</th><th>วงเงินต่อใบ</th><th>ใบเสร็จ</th><th /></tr></thead>
                <tbody>
                  {policyRows.map((p) => (
                    <tr key={p.category} style={{ cursor: 'default' }}>
                      <td>{p.category}</td>
                      <td className="num">{money(p.limit_satang)}</td>
                      <td className="dim">{p.requires_receipt ? 'ต้องแนบ' : 'ไม่บังคับ'}</td>
                      <td className="num">
                        <button className="btn btn-sm" onClick={() => startPolicy(p)}>แก้ไข</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">{editingPolicy ? 'แก้ไขนโยบาย' : 'เพิ่มนโยบาย'}</div>
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="policy-category">หมวดค่าใช้จ่าย</label>
            <input id="policy-category" className="input" style={{ width: '100%' }} maxLength={80}
              value={policyCategory} onChange={(e) => setPolicyCategory(e.target.value)} placeholder="เช่น เดินทาง" />
          </div>
          <div className="field">
            <label htmlFor="policy-limit">วงเงินต่อใบ (บาท)</label>
            <input id="policy-limit" className="input tnum" style={{ width: '100%' }} type="number" min="0" step="0.01"
              value={policyLimit} onChange={(e) => setPolicyLimit(e.target.value)} placeholder="0.00" />
          </div>
          <label className="row" style={{ gap: 8, fontSize: 12.5, marginTop: 4 }}>
            <input type="checkbox" checked={policyReceipt} onChange={(e) => setPolicyReceipt(e.target.checked)} />
            ต้องแนบใบเสร็จทุกใบ
          </label>
          <div className="row" style={{ marginTop: 16, gap: 8 }}>
            <button
              className="btn btn-primary"
              disabled={savePolicy.isPending || !policyCategory.trim() || toSatang(policyLimit) <= 0}
              onClick={() => savePolicy.mutate()}
            >
              {savePolicy.isPending ? 'กำลังบันทึก…' : 'บันทึกนโยบาย'}
            </button>
            {editingPolicy && (
              <button className="btn" onClick={() => startPolicy()}>ล้างฟอร์ม</button>
            )}
          </div>
          {savePolicy.error && <div className="sev sev-critical" style={{ marginTop: 10 }}><i className="sev-dot" />{(savePolicy.error as Error).message}</div>}
        </div>
      </div>
    </>
  )
}
